import { ChangeEvent, useCallback, useEffect, useState } from "react";
import { Dayjs } from "dayjs";
import { ALL_OPTION, PAYMENT_CHANNEL_FILTER, STATUS_FILTER } from "./data";
import {
  ALL_DATES_MIN_BILL_NUMBER_LENGTH,
  BILL_NUMBER_MAX_LENGTH,
  SalesListFilters,
  isBillNumberSearch,
} from "./salesListFilters";
import { getAllBranch, getAllRoutes, getAllRoutesByCompany } from "../../services";
import { SaleRoute } from "../../types/sale-route.type";
import { useUserRole } from "../../utils/hooks/useUserRole";
import { Branch } from "../../types/user.type";
import DateRangePicker, { WITHIN_A_MONTH_PRESET_GROUPS } from "../common/DateRangePicker";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormHelperText from "@mui/material/FormHelperText";
import Switch from "@mui/material/Switch";

type FieldChangeEvent = ChangeEvent<HTMLInputElement | HTMLTextAreaElement>;

interface SalesFilterProps {
  filters: SalesListFilters;
  onChange: (patch: Partial<SalesListFilters>) => void;
}

interface FilterFieldProps {
  label: string;
  /** A line under the control, kept out of the row so the label stays level with the control. */
  helperText?: string;
  children: React.ReactNode;
}

// Fits the longest labels (ชำระโดย, ชื่อลูกค้า: 56px in the app font) so every field in a column
// starts at the same x. The label never shrinks, so a wider one pushes its field instead of
// being cut off.
const FILTER_LABEL_MIN_WIDTH = 60;
/** Between a label and its control, in theme spacing units. */
const LABEL_GAP = 1;

// Indented by the label column (every label here fits FILTER_LABEL_MIN_WIDTH) plus the gap, so a
// helper line starts under its control rather than under its label.
const HELPER_TEXT_SX = { ml: `${FILTER_LABEL_MIN_WIDTH}px`, pl: LABEL_GAP } as const;

const FilterField = ({ label, helperText, children }: FilterFieldProps) => (
  <Grid size={{ xs: 12, sm: 3 }}>
    <Stack direction="row" alignItems="center" spacing={LABEL_GAP}>
      <Typography
        variant="body2"
        noWrap
        sx={{ minWidth: FILTER_LABEL_MIN_WIDTH, flexShrink: 0 }}
      >
        {label}
      </Typography>
      <Box sx={{ flexGrow: 1, minWidth: 0 }}>{children}</Box>
    </Stack>
    {helperText && <FormHelperText sx={HELPER_TEXT_SX}>{helperText}</FormHelperText>}
  </Grid>
);

// FormHelperText renders a lone space as an empty line of full height (MUI's reserved-space
// idiom). The line under the date range is always there, so the first character typed into
// เลขที่บิล never pushes the rows below down.
const BLANK_HELPER_LINE = " ";

/** The line under the date range: how the entered bill number is being searched. */
const dateRangeHelperFor = (transactionNo: string): string => {
  if (isBillNumberSearch(transactionNo)) return "ค้นทุกวันที่เมื่อกรอกเลขที่บิล";
  if (transactionNo.trim()) {
    return `กรอกเลขที่บิลอย่างน้อย ${ALL_DATES_MIN_BILL_NUMBER_LENGTH} ตัวเพื่อค้นทุกวันที่`;
  }
  return BLANK_HELPER_LINE;
};

// Fields run in one wrapping row of quarter-width cells, วันที่ first, so no field leaves a gap.
// Head office (with สาขา and รหัส) gets 4 · 4 · 2 cells: วันที่ · สาขา · สายส่ง · เลขที่บิล / สถานะ ·
// ชำระโดย · Tax ID · ชื่อลูกค้า / รหัส · toggle. Every other role gets two whole rows: วันที่ ·
// สายส่ง · เลขที่บิล · สถานะ / ชำระโดย · Tax ID · ชื่อลูกค้า · toggle.
export default function SalesFilter({ filters, onChange }: SalesFilterProps) {
  const { isHeadOfficeAccounting } = useUserRole();
  const [saleRoutes, setSaleRoutes] = useState<SaleRoute[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const { companyId } = filters;

  const getRoutes = useCallback(async () => {
    if (isHeadOfficeAccounting) {
      if (companyId === ALL_OPTION) {
        setSaleRoutes([]);
        return;
      }
      const {
        data: { data },
      } = await getAllRoutes(companyId, { includeSpecial: true });
      setSaleRoutes(Array.isArray(data) ? data : data.rows ?? []);
    } else {
      const {
        data: { data },
      } = await getAllRoutesByCompany({ includeSpecial: true });
      setSaleRoutes(data);
    }
  }, [isHeadOfficeAccounting, companyId]);

  useEffect(() => {
    getRoutes();
  }, [getRoutes]);

  const isRouteDisabled = isHeadOfficeAccounting && companyId === ALL_OPTION;
  const isDateRangeIgnored = isBillNumberSearch(filters.transactionNo);
  const dateRangeHelper = dateRangeHelperFor(filters.transactionNo);

  const onGetAllBranch = useCallback(async () => {
    const {
      data: { data },
    } = await getAllBranch();
    setBranches(data);
  }, []);

  useEffect(() => {
    onGetAllBranch();
  }, [onGetAllBranch]);

  // Routes belong to a branch, so a new branch starts the route over.
  const changeBranch = (event: FieldChangeEvent) =>
    onChange({ companyId: event.target.value, routeCode: ALL_OPTION });
  const changeRoute = (event: FieldChangeEvent) =>
    onChange({ routeCode: event.target.value });
  const changeTransactionNo = (event: FieldChangeEvent) =>
    onChange({ transactionNo: event.target.value });
  const changeStatus = (event: FieldChangeEvent) =>
    onChange({ status: event.target.value });
  const changePaymentType = (event: FieldChangeEvent) =>
    onChange({ paymentType: event.target.value });
  const changeTaxId = (event: FieldChangeEvent) =>
    onChange({ taxId: event.target.value });
  const changeCustomerName = (event: FieldChangeEvent) =>
    onChange({ customerName: event.target.value });
  const changeCustomerCode = (event: FieldChangeEvent) =>
    onChange({ customerCode: event.target.value });
  const changeReplaceFlagged = (event: ChangeEvent<HTMLInputElement>) =>
    onChange({ replaceFlagged: event.target.checked });

  const changeDateRange = (dateFrom: Dayjs, dateTo: Dayjs) => onChange({ dateFrom, dateTo });

  return (
    <Grid container spacing={2} mb={2}>
      <FilterField label="วันที่" helperText={dateRangeHelper}>
        <DateRangePicker
          dateFrom={filters.dateFrom}
          dateTo={filters.dateTo}
          onChange={changeDateRange}
          presetGroups={WITHIN_A_MONTH_PRESET_GROUPS}
          disabled={isDateRangeIgnored}
        />
      </FilterField>
      {isHeadOfficeAccounting && (
        <FilterField label="สาขา">
          <TextField
            select
            size="small"
            fullWidth
            value={companyId}
            onChange={changeBranch}
          >
            <MenuItem value={ALL_OPTION}>ทั้งหมด</MenuItem>
            {branches.map((branch) => (
              <MenuItem key={branch.uuid} value={branch.company_id}>
                {branch.branch_code} - {branch.branch_name}
              </MenuItem>
            ))}
          </TextField>
        </FilterField>
      )}
      <FilterField label="สายส่ง">
        <TextField
          select
          size="small"
          fullWidth
          disabled={isRouteDisabled}
          value={filters.routeCode}
          onChange={changeRoute}
          helperText={isRouteDisabled ? "เลือกสาขาก่อน" : undefined}
        >
          <MenuItem value={ALL_OPTION}>ทั้งหมด</MenuItem>
          {saleRoutes.map((route) => (
            <MenuItem key={route.route_code} value={route.route_code}>
              {route.route_code} - {route.route_name}
            </MenuItem>
          ))}
        </TextField>
      </FilterField>
      <FilterField label="เลขที่บิล">
        <TextField
          size="small"
          fullWidth
          placeholder="กรอกเลขที่บิล"
          value={filters.transactionNo}
          onChange={changeTransactionNo}
          slotProps={{ htmlInput: { maxLength: BILL_NUMBER_MAX_LENGTH } }}
        />
      </FilterField>
      <FilterField label="สถานะ">
        <TextField
          select
          size="small"
          fullWidth
          value={filters.status}
          onChange={changeStatus}
        >
          {STATUS_FILTER.map((item) => (
            <MenuItem key={item.value} value={item.value}>
              {item.label}
            </MenuItem>
          ))}
        </TextField>
      </FilterField>
      <FilterField label="ชำระโดย">
        <TextField
          select
          size="small"
          fullWidth
          value={filters.paymentType}
          onChange={changePaymentType}
        >
          {PAYMENT_CHANNEL_FILTER.map((item) => (
            <MenuItem key={item.value} value={item.value}>
              {item.label}
            </MenuItem>
          ))}
        </TextField>
      </FilterField>
      <FilterField label="Tax ID">
        <TextField
          size="small"
          fullWidth
          placeholder="กรอก Tax ID"
          value={filters.taxId}
          onChange={changeTaxId}
        />
      </FilterField>
      <FilterField label="ชื่อลูกค้า">
        <TextField
          size="small"
          fullWidth
          placeholder="กรอกชื่อลูกค้า"
          value={filters.customerName}
          onChange={changeCustomerName}
        />
      </FilterField>
      {isHeadOfficeAccounting && (
        <FilterField label="รหัส">
          <TextField
            size="small"
            fullWidth
            placeholder="กรอกรหัสลูกค้า"
            value={filters.customerCode}
            onChange={changeCustomerCode}
          />
        </FilterField>
      )}
      {/* Replacement bills whose GPS was flagged at replace time (anything but OK) —
          the ones finance should check with the salesperson before approving. */}
      <Grid size={{ xs: 12, sm: 3 }} display="flex" alignItems="center">
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={filters.replaceFlagged}
              onChange={changeReplaceFlagged}
            />
          }
          label={<Typography variant="body2">เฉพาะบิลแทนที่ติดธง</Typography>}
        />
      </Grid>
    </Grid>
  );
}
